/**
 * CRM-ready sidebar navigation for TIC Comms.
 *
 * Sections:
 *   MAIN     — Inbox, Search (business-aware), Contacts, Templates
 *   SYSTEM   — Settings, Admin (role-gated)
 *
 * Desktop: floating rounded panel (left-4, top-4, bottom-4, w-[220px])
 * Mobile: slide-in drawer with overlay backdrop
 */

import { useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  Tray,
  MagnifyingGlass,
  AddressBook,
  FileText,
  GearSix,
  ShieldCheck,
  UsersThree,
  Kanban,
  ListChecks,
  UserFocus,
  X,
} from '@phosphor-icons/react'
import { useAuth } from '@/contexts/AuthContext'
import { useBusiness } from '@/contexts/BusinessContext'
import { useLayout } from '@/contexts/LayoutContext'
import type { ComponentType } from 'react'
import type { IconProps } from '@phosphor-icons/react'

// ============================================================
// Navigation configuration
// ============================================================

interface NavItem {
  to: string
  icon: ComponentType<IconProps>
  label: string
  end?: boolean
  /** If true, item is shown but greyed out with "Soon" badge */
  comingSoon?: boolean
  /** Minimum role needed (null = always show) */
  requireAdmin?: boolean
}

/**
 * Static nav items appended after the per-business links.
 * Inbox/Search/Pipeline are built dynamically below — Inbox's `to` follows
 * the currently-selected business so clicking it inside a business doesn't
 * dump you back to the aggregate "All Businesses" view.
 */
const staticMainNavItems: NavItem[] = [
  { to: '/contacts', icon: AddressBook, label: 'Contacts' },
  { to: '/tasks', icon: ListChecks, label: 'Tasks' },
  { to: '/templates', icon: FileText, label: 'Templates' },
]

const systemNavItems: NavItem[] = [
  { to: '/team', icon: UsersThree, label: 'Team', requireAdmin: true },
  { to: '/settings', icon: GearSix, label: 'Settings' },
  { to: '/admin', icon: ShieldCheck, label: 'Admin', requireAdmin: true },
]

// ============================================================
// Component
// ============================================================

interface SidebarProps {
  mobileOpen: boolean
  onMobileClose: () => void
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const { memberships } = useAuth()
  const { currentBusiness } = useBusiness()
  const { sidebarHidden } = useLayout()
  const isAdmin = memberships.some(
    (m) => m.role === 'admin' || m.role === 'owner'
  )

  // Close mobile drawer on Escape key
  useEffect(() => {
    if (!mobileOpen) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onMobileClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [mobileOpen, onMobileClose])

  // Build main nav items dynamically. Inbox always shows; its target follows
  // the current business so clicking it from inside a business stays inside
  // that business. Search + Pipeline only appear when a business is in
  // context (they're per-business routes with no aggregate equivalent).
  const mainNavItems: NavItem[] = (() => {
    const items: NavItem[] = []
    // My work is pinned at the top — it's the staff-scoped default landing
    // for line users. Per docs/ux-direction.md the "see everything assigned
    // to me" target is 1 click; this link is that 1 click.
    items.push({
      to: '/my-work',
      icon: UserFocus,
      label: 'My work',
    })
    items.push({
      to: currentBusiness ? `/inbox/${currentBusiness.id}` : '/inbox',
      icon: Tray,
      label: 'Inbox',
      // `end` keeps the active class only on the exact match — without it
      // /inbox/{id}/conversations/{cid} would also light up the link.
      end: !currentBusiness,
    })
    if (currentBusiness) {
      items.push(
        {
          to: `/search/${currentBusiness.id}`,
          icon: MagnifyingGlass,
          label: 'Search',
        },
        {
          to: `/pipeline/${currentBusiness.id}`,
          icon: Kanban,
          label: 'Pipeline',
        },
      )
    }
    items.push(...staticMainNavItems)
    return items
  })()

  function renderNavItem(item: NavItem, index: number) {
    // Hide admin-only items for non-admins
    if (item.requireAdmin && !isAdmin) return null

    // Coming soon items — render as disabled div, not a link
    if (item.comingSoon) {
      return (
        <div
          key={item.to}
          style={{ '--stagger-index': index } as React.CSSProperties}
          className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[15px] stagger-in text-text-muted cursor-default select-none"
        >
          <item.icon size={18} weight="light" className="shrink-0 opacity-40" />
          <span className="opacity-50">{item.label}</span>
          <span className="ml-auto text-[10px] font-medium uppercase tracking-wider opacity-40">
            Soon
          </span>
        </div>
      )
    }

    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        onClick={onMobileClose}
        style={{ '--stagger-index': index } as React.CSSProperties}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[15px] transition-colors stagger-in',
            isActive
              ? 'bg-accent/8 text-accent font-medium'
              : 'text-text-secondary hover:text-text-primary hover:bg-black/[0.03]'
          )
        }
      >
        <item.icon size={18} weight="light" className="shrink-0" />
        {item.label}
      </NavLink>
    )
  }

  const navContent = (
    <>
      {/* Logo */}
      <div className="h-14 flex items-center px-5">
        <span className="text-lg font-semibold tracking-tight text-text-primary">
          TIC Comms
        </span>
      </div>

      {/* Divider */}
      <div className="mx-4 border-t border-border/50" />

      {/* Main navigation */}
      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {mainNavItems.map((item, i) => renderNavItem(item, i))}
      </nav>

      {/* System section */}
      <div className="px-3 pb-4">
        <div className="mx-1 mb-2 border-t border-border/50" />
        <div className="space-y-0.5">
          {systemNavItems.map((item, i) =>
            renderNavItem(item, mainNavItems.length + i)
          )}
        </div>
      </div>
    </>
  )

  return (
    <>
      {/* Desktop sidebar — floating panel. Hidden when `sidebarHidden`
          (deck view), at which point the burger-overlay below drives
          visibility on every breakpoint. */}
      <aside
        aria-label="Main navigation"
        className={cn(
          'fixed left-4 top-4 bottom-4 w-[220px] rounded-[1.25rem] bg-white border border-border/50 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_-8px_rgba(0,0,0,0.06)] flex-col z-30',
          sidebarHidden ? 'hidden' : 'hidden md:flex',
        )}
      >
        {navContent}
      </aside>

      {/* Overlay backdrop — mobile by default, every breakpoint when
          sidebarHidden is on. */}
      {mobileOpen && (
        <div
          className={cn(
            'fixed inset-0 bg-black/20 backdrop-blur-sm z-50',
            !sidebarHidden && 'md:hidden',
          )}
          onClick={onMobileClose}
          role="button"
          aria-label="Close navigation menu"
          tabIndex={-1}
        />
      )}

      {/* Slide-in drawer — mobile by default, every breakpoint when
          sidebarHidden is on. */}
      <aside
        className={cn(
          'fixed left-0 top-0 bottom-0 w-[260px] bg-white border-r border-border/50 shadow-elevated flex flex-col z-50',
          !sidebarHidden && 'md:hidden',
          'transition-transform duration-300 ease-out',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Navigation menu"
      >
        {/* Close button */}
        <button
          onClick={onMobileClose}
          aria-label="Close navigation menu"
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-black/[0.04] text-text-muted transition-colors focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:outline-none"
        >
          <X size={18} weight="light" />
        </button>
        {navContent}
      </aside>
    </>
  )
}
